/**
 * Admin Control Centre — one central landing for all configurable masters.
 * Grouped into 11 sections as per the governance spec.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Building2, Users, Package, Wrench, Truck, DollarSign, GitBranch,
  ShieldCheck, Plug, MessageSquare, Cog, ArrowRight, ArrowUpRight,
  Search, Trash2,
} from "lucide-react";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";

const GROUP_META = {
  organisation:  { title: "Organisation",       icon: Building2, hint: "Master companies, companies, sites, countries, regions." },
  people:        { title: "People",             icon: Users,     hint: "Employees, departments, roles, statuses, verification." },
  catalogue:     { title: "Assets & catalogue", icon: Package,   hint: "Brands, models, SKUs, condition grades." },
  services:      { title: "Services",           icon: Wrench,    hint: "Service catalogue, categories, SLA, workflows." },
  operations:    { title: "Operations",         icon: Truck,     hint: "Warehouses, zones, racks, courier, site types." },
  commercial:    { title: "Commercial",         icon: DollarSign,hint: "Currencies, taxes, payment terms, rate cards, discounts." },
  workflows:     { title: "Workflows",          icon: GitBranch, hint: "Approval rules, statuses, notification templates." },
  security:      { title: "Security",           icon: ShieldCheck, hint: "Roles, permissions, MFA, audit retention." },
  integrations:  { title: "Integrations",       icon: Plug,      hint: "Couriers, HRMS, MDM, storage providers." },
  communication: { title: "Communication",      icon: MessageSquare, hint: "Email templates, in-app notices, feedback." },
  system:        { title: "System",             icon: Cog,       hint: "Feature flags, imports, backups, recycle bin." },
};

// Custom landing links to purpose-built admin pages already in the app
const CUSTOM_LINKS = {
  organisation: [
    { label: "Master companies", to: "/customers" },
    { label: "Companies", to: "/companies" },
    { label: "Customer sites", to: "/admin/sites" },
    { label: "Location Master (India)", to: "/location-master" },
  ],
  people: [
    { label: "Users & roles", to: "/users" },
    { label: "Employees", to: "/employees" },
  ],
  system: [
    { label: "Release console", to: "/governance" },
    { label: "Import history", to: "/admin/imports" },
  ],
  security: [
    { label: "Force-override report", to: "/admin/audit/force-overrides" },
    { label: "Legal acceptance framework", to: "/admin/legal" },
  ],
  communication: [
    { label: "Advance notices", to: "/notices" },
    { label: "Customer feedback", to: "/feedback" },
  ],
};

export default function AdminControlCentre() {
  const [registry, setRegistry] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/registry").then(({ data }) => setRegistry(data.groups || {}));
  }, []);

  const filterFn = (label) => !q.trim() || label.toLowerCase().includes(q.trim().toLowerCase());

  return (
    <div className="max-w-6xl space-y-8" data-testid="admin-control-centre">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Administration</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Control Centre</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Configure every business parameter here — no developer, no deployment. Every master supports Add, Edit,
          Duplicate, Deactivate, Delete, Restore, Import and Export. Delete is dependency-aware.
        </p>
      </div>
      <QuickActionGroup module="admin" className="mb-0" />

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search masters…"
            className="input pl-8" data-testid="admin-search" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(GROUP_META).map(([groupKey, meta]) => {
          const items = (registry?.[groupKey] || []).filter((it) => filterFn(it.label));
          const links = (CUSTOM_LINKS[groupKey] || []).filter((l) => filterFn(l.label));
          if (!items.length && !links.length) return null;
          return (
            <section key={groupKey} className="bg-white border border-slate-200 rounded-md" data-testid={`admin-group-${groupKey}`}>
              <div className="flex items-start gap-3 px-4 py-3 border-b border-slate-100">
                <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center">
                  <meta.icon className="h-4 w-4" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{meta.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{meta.hint}</div>
                </div>
              </div>
              <ul className="divide-y divide-slate-50">
                {links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="flex items-center justify-between px-4 py-2.5 text-sm text-slate-800 hover:bg-slate-50" data-testid={`admin-link-${l.to.replaceAll('/','-')}`}>
                      <span>{l.label}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 text-slate-400" />
                    </Link>
                  </li>
                ))}
                {items.map((m) => (
                  <li key={m.key}>
                    <Link to={`/admin/masters/${m.key}`} className="flex items-center justify-between px-4 py-2.5 text-sm text-slate-800 hover:bg-slate-50" data-testid={`admin-master-${m.key}`}>
                      <span>{m.label}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <div className="border-t border-slate-100 pt-4 text-[11px] text-slate-500 flex items-center gap-2">
        <Trash2 className="h-3.5 w-3.5" />
        <span className="text-slate-400" title="A central recycle bin is not implemented yet — restore archived quotes from the Quotations archive filter">Recycle bin (not available)</span>
        <span className="mx-2">·</span>
        Deleted masters that had transactional history are retained here, not destroyed.
      </div>
    </div>
  );
}
